<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import ProviderSearch from '@/components/Header/ProviderSearch.vue'
import SubmissionComposer from '@/components/SubmissionComposer.vue'
import { useMainStore } from '@/stores/main'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  sidebarExpanded: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const mainStore = useMainStore()
const userStore = useUserStore()

// Admin check
const isAdmin = computed(() => userStore.accountType === 1337)

// Open submission modal
function openSubmissionModal() {
  mainStore.show_modal(SubmissionComposer, {})
}

// Open notifications page
function openNotifications() {
  router.push({ name: 'Notifications' })
}

// Scroll handling
const isScrolled = ref(false)
let scrollContainer = null

const handleScroll = () => {
  if (scrollContainer) {
    isScrolled.value = scrollContainer.scrollTop > 10
  }
}

onMounted(() => {
  // Find the scrollable content wrapper (parent of header)
  const header = document.querySelector('.dashboard-header')
  if (header && header.parentElement) {
    // Find the scrollable container
    scrollContainer = header.parentElement.querySelector('div[style*="overflow: auto"]')
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll)
      handleScroll()
    }
  }
})

onUnmounted(() => {
  if (scrollContainer) {
    scrollContainer.removeEventListener('scroll', handleScroll)
  }
})
</script>

<template>
  <header
    class="dashboard-header"
  >
    <div class="backdrop"></div>
    <div class="header-content-wrapper">
      <div class="header-content">
        <ProviderSearch v-if="isAdmin" />
        <div class="header-actions">
          <!--
          <button v-if="isAdmin" class="button" @click="openSubmissionModal">
            Post
          </button>
          -->
          <router-link to="/articles" class="articles-button">
            <font-awesome-icon :icon="['fas', 'newspaper']" />
          </router-link>
          <router-link to="/notifications" class="bell-button">
            <font-awesome-icon :icon="['fas', 'bell']" />
            <span v-if="mainStore.has_unread_notifications" class="notification-badge"></span>
          </router-link>
          <router-link to="/account" class="account-button">
            <font-awesome-icon :icon="['fas', 'user-astronaut']" />
          </router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.dashboard-header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.75rem 0.75rem;
  margin: 1rem 1rem 1rem 0;
  z-index: 10;
}

.backdrop {
  background: #151a22;
  position: absolute;
  inset: 0;
  border-radius: 1rem;
  pointer-events: none;
  z-index: 0;
}

.header-content-wrapper {
  width: 100%;
  position: relative;
  z-index: 1;
}

.header-content {
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.bell-button {
  position: relative;
  min-height: 3rem;
  width: 3rem;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  color: #7A8696;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.1rem;
  text-decoration: none;
}

.button {
  background-color: #1d2531;
  min-height: 3rem;
  border: none;
  border-radius: 0.75rem;
  color: #7A8696;
  display: flex;
  font-weight: normal;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  padding: 0 1.5rem;
}


.button:hover {
  background-color: #1d2531;
  color: white;
}

.bell-button:hover {
  background-color: #1d2531;
  color: white;
}

.bell-button.router-link-active,
.bell-button.router-link-exact-active {
  background-color: #1d2531;
  color: white !important;
}

.notification-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid #141821;
  animation: pulse-badge 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-badge {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.account-button {
  min-height: 3rem;
  width: 3rem;
  border-radius: 0.75rem;
  background: transparent;
  color: #7A8696;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  text-decoration: none;
  font-size: 1.1rem;
}

.account-button:hover {
  background: #1d2531;
  color: white;
}

.account-button.router-link-active,
.account-button.router-link-exact-active {
  background-color: #1d2531;
  color: white !important;
}

.articles-button {
  min-height: 3rem;
  width: 3rem;
  border-radius: 0.75rem;
  background: transparent;
  color: #7A8696;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  text-decoration: none;
  font-size: 1.1rem;
}

.articles-button:hover {
  background: #1d2531;
  color: white;
}

.articles-button.router-link-active,
.articles-button.router-link-exact-active {
  background-color: #1d2531;
  color: white !important;
}

.submit-button {
  padding: 0.6rem 2rem;
  background-color: #5094ED;
  font-weight: bold;
  outline: none;
  box-shadow: none;
  border: none;
  color: white;
  border-radius: 30px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: "Inter", sans-serif;
}

.submit-button:hover {
  background-color: #63A1F2;
}
</style>
